import { useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarDays, Clock } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const pad = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromISODate = (s) => { if (!s) return undefined; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const SLOTS = (() => { const out = []; for (let h = 8; h <= 19; h++) for (const m of [0, 30]) { if (h === 19 && m === 30) break; out.push(`${pad(h)}:${pad(m)}`); } return out; })();
const slotLabel = (t) => { const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "pm" : "am"; return `${((h + 11) % 12) + 1}:${pad(m)} ${ap}`; };

/** Calendar + 30-min slot picker. value = { date: 'YYYY-MM-DD', time: 'HH:MM' }. */
export function DateTimePicker({ date, time, onChange, tzLabel = "AEDT" }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => fromISODate(date), [date]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isToday = selected && selected.getTime() === today.getTime();
  const nowMins = new Date().getHours() * 60 + new Date().getMinutes();
  const btn = "w-full rounded-xl bg-white border border-[#E4E0D6] hover:border-[#0B0D10] focus:border-[#0B0D10] outline-none px-3.5 h-11 text-[13.5px] text-left inline-flex items-center gap-2.5 transition-colors";
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="booking-datetime">
      <div>
        <label className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/60 font-medium block mb-1.5">Date</label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" data-testid="booking-date" className={btn}>
              <CalendarDays size={15} className="text-[#3a3d44]/50 shrink-0" />
              <span className={selected ? "text-[#0B0D10] font-medium" : "text-[#3a3d44]/45"}>{selected ? format(selected, "EEE d MMM") : "Pick a day"}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="p-0 w-auto rounded-2xl border-[#E4E0D6] shadow-[0_24px_60px_-20px_rgba(11,13,16,0.35)]" data-testid="booking-calendar">
            <Calendar mode="single" selected={selected} disabled={{ before: today }} initialFocus
              onSelect={(d) => { if (d) { onChange({ date: toISODate(d), time }); setOpen(false); } }}
              classNames={{ day_selected: "bg-[#0B0D10] text-[#C8FF4D] hover:bg-[#0B0D10] hover:text-[#C8FF4D] focus:bg-[#0B0D10] focus:text-[#C8FF4D] rounded-lg font-bold", day_today: "bg-[#C8FF4D]/40 rounded-lg font-semibold" }} />
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <label className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/60 font-medium block mb-1.5">Time <span className="normal-case font-normal tracking-normal">({tzLabel})</span></label>
        <div className="relative">
          <Clock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#3a3d44]/50 pointer-events-none" />
          <select data-testid="booking-time" value={time} onChange={(e) => onChange({ date, time: e.target.value })}
            className={`${btn} pl-10 appearance-none ${time ? "text-[#0B0D10] font-medium" : "text-[#3a3d44]/45"}`}>
            <option value="">Pick a slot</option>
            {SLOTS.map((t) => { const [h, m] = t.split(":").map(Number); const past = isToday && h * 60 + m <= nowMins; return <option key={t} value={t} disabled={past}>{slotLabel(t)}{past ? " · past" : ""}</option>; })}
          </select>
        </div>
      </div>
    </div>
  );
}
